import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import "./Exporter.scss";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { Group, Mesh, Object3D } from "three";
import { applyProperties } from "../../helpers/3d";
import { clone, first, unset } from 'lodash';
import * as THREE from "three";
import { readFile } from "../../helpers/files";
import { ISceneData } from "../../interfaces/ISceneData";

export const delimiter = '__';

export function Exporter() {
    const canvasRef = useRef(null);
    const [objects, setObjects] = useState<string[]>(JSON.parse(window.localStorage.getItem('objects') ?? '[]'));
    const [sceneData, setSceneData] = useState<ISceneData[]>(JSON.parse(window.localStorage.getItem('sceneData') ?? '[]'));
    const [sceneCount, setSceneCount] = useState(sceneData.length);
    const [isDraggingOnField, setIsDraggingOnField] = useState<[number, string]|false>(false);
    const [canvasScene, setCanvasScene] = useState<any>();
    const [activeScenes, setActiveScenes] = useState<number[]>([]);
    const [gltf, setGLTF] = useState<GLTF>();
    const [canvasDragPosition, setCanvasDragPosition] = useState<[number, number]|false>(false);

    // Save the sceneCount / sceneData for future use
    useEffect(() => {
        const newSceneData: ISceneData[] = clone(sceneData);
        let updateState = false;
        const newSceneCount = sceneCount > 0 ? sceneCount : 1;
        for(let i = 0; i < newSceneCount; i++) {
            if (!newSceneData[i]) {
                newSceneData[i] = {
                    name: '',
                    properties: {},
                };
                updateState = true;
            }
        }
        if (updateState) {
            setSceneData(newSceneData);
        }
    }, [sceneCount, sceneData]);
    useEffect(() => {
        window.localStorage.setItem('sceneData', JSON.stringify(sceneData));
    }, [sceneData]);

    useEffect(() => {
        window.localStorage.setItem('objects', JSON.stringify(objects));
    }, [objects]);

    useEffect(() => {
        const canvas = canvasRef.current as any as HTMLCanvasElement;
        if (canvas) {
            const parent = canvas.parentElement as any as HTMLDivElement;

            const scene = new THREE.Scene();

            const camera = new THREE.PerspectiveCamera( 75, parent.clientWidth / parent.clientHeight, 1, 1000 );
            const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
            renderer.setPixelRatio(window.devicePixelRatio);
            renderer.setSize( parent.clientWidth, parent.clientHeight );
            renderer.shadowMap.enabled = true


            // Ambient light
            const ambientLight = new THREE.AmbientLight()
            ambientLight.color = new THREE.Color(0xffffff)
            ambientLight.intensity = 0.5
            scene.add(ambientLight)


            // Directional light
            const directionalLight = new THREE.DirectionalLight(0xFFFFFF, 2);
            directionalLight.position.set(0, 2, 4);
            directionalLight.castShadow = true;
            directionalLight.shadow.camera.near = 1
            directionalLight.shadow.camera.far = 6

            scene.add(directionalLight);

            camera.position.y = 0;

            camera.position.z = 3;

            const light = new THREE.HemisphereLight( 0xffffbb, 0x080820, 1 );
            scene.add( light );

            setCanvasScene(scene);

            const animate = function () {
                requestAnimationFrame( animate );

                renderer.render( scene, camera );
            };
            animate();
        }
    }, [canvasRef]);

    useEffect(() => {
        if (!gltf) return;
        let activeProperties: {[key: string]: number} = {};
        sceneData.forEach((row, index) => {
            if (activeScenes.includes(index)) {
                activeProperties = {
                    ...activeProperties,
                    ...row.properties,
                }
            }
        });
        applyProperties(gltf, activeProperties);
    }, [activeScenes, sceneData, gltf]);

    const handleChange = (e: ChangeEvent) => {
        const input = e.target as HTMLInputElement;
        readFile(input, parseContents);
    }
    const handleCSVChange = (e: ChangeEvent) => {
        const input = e.target as HTMLInputElement;
        readFile(input, (csvContent) => {
            const contents = atob(csvContent.split('base64,')[1]);
            const lines = contents.split("\r\n");
            const rows = lines.map(line => line.split(';'));

            // Retrieve the names of the scenes and the count
            const firstRow: string[] = first(rows) as string[];
            firstRow.shift();
            const sceneNames = clone(firstRow);

            // Remove the first row
            rows.shift();

            // Retrieve all objects
            const newObjects = rows.map(row => row[0]);

            // Set all new sceneData
            const newSceneData: ISceneData[] = [];
            sceneNames.forEach((sceneName, sceneIndex) => {
                const filteredProperties: {[key: string]: number} = {};
                rows.forEach((row) => {
                    const name = row[0];
                    const value = row[sceneIndex + 1];
                    if ((value ?? '').length > 0) {
                        filteredProperties[name] = parseFloat(value.split(',').join('.'));
                    }
                });
                newSceneData.push({
                    name: sceneName,
                    properties: filteredProperties,
                });
            });

            setObjects(newObjects);
            setSceneData(newSceneData);
            setSceneCount(sceneNames.length);
            // console.log({ rows, sceneNames, newObjects, newSceneData });
        });
    };

    const parseContents = (contents: string) => {
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(
            contents,
            (gltf) => {
                const scene = gltf.scene;

                let names: string[] = [];

                const fixName = (name: string): string => {
                    return name;
                }

                // Get objects names
                const findNames = (parent: Mesh | Group | Object3D): string[] => {
                    let names: string[] = [];
                    if (fixName(parent.name).includes(delimiter)) {
                        const [joinedProperties, elementsName] = fixName(parent.name).split(delimiter);
                        const properties = joinedProperties.split(',');
                        properties.forEach((property) => {
                            const name = `${property}${delimiter}${elementsName}`;
                            names.push(name);
                        });
                    }
                    if (parent.children?.length > 0) {
                        parent.children.forEach((child: Mesh | Group | Object3D) => {
                            names = [
                                ...names,
                                ...(findNames(child))
                            ];
                        })
                    }
                    return names;
                }
                names = findNames(scene);

                // Get morph action names
                const morphActions = gltf.animations.filter(animation => animation.name.split(delimiter)[0] === 'Morph');
                names = [
                    ...names,
                    ...morphActions.map(animation => animation.name),
                ];

                names.sort();

                setObjects(names);
                setGLTF(gltf);
                canvasScene?.add(scene);

                const mixer = new THREE.AnimationMixer(scene);
                gltf.animations.forEach((animation) => {
                    if (!animation.name.toLowerCase().includes('morph')) {
                        mixer.clipAction(animation).play();
                    }
                });
                const clock = new THREE.Clock();
                let previousTime = 0;

                const animate = () => {
                    const elapsedTime = clock.getElapsedTime();
                    const deltaTime = elapsedTime - previousTime;
                    previousTime = elapsedTime;
                    if(mixer) mixer.update(deltaTime);

                    // scene.rotation.y -= 0.005;
                    requestAnimationFrame(animate);
                }
                animate();
            }
        );
    }

    const setSceneName = (value: string, sceneIndex: number) => {
        setSceneData(prevState => {
            const newState = clone(prevState);
            if (!newState[sceneIndex]) {
                newState[sceneIndex] = {
                    name: '',
                    properties: {},
                };
            }
            newState[sceneIndex].name = value;
            newState[sceneIndex].properties = {};
            return newState;
        })
    }

    const handleDeltaChange = (value: string | number | undefined, property: string, index: number) => {
        setSceneData((prevState) => {
            const newSceneData: ISceneData[] = clone(prevState);
            if (value !== undefined && !isNaN(parseFloat(value.toString()))) {
                newSceneData[index].properties[property] = parseFloat(value.toString());
            } else {
                unset(newSceneData[index].properties, property);
            }
            return newSceneData;
        });
    }

    const copyFieldDataIfDragging = (currentSceneIndex: number, currentName: string) => {
        if (isDraggingOnField) {
            const [sceneIndex, name] = isDraggingOnField;
            if (sceneIndex === currentSceneIndex || name === currentName) {
                const value = sceneData[sceneIndex].properties[name];
                handleDeltaChange(value, currentName, currentSceneIndex);
            }
        }
    };

    const toggleActiveScene = (sceneIndex: number) =>{
        setActiveScenes((prevState) => {
            const newState: number[] = clone(prevState);
            if (newState.includes(sceneIndex)) {
                return newState.filter(i => i !== sceneIndex);
            }
            newState.push(sceneIndex);
            return newState;
        })
    }

    const downloadCSV = () => {
        const firstLine = sceneData
            .map((row, index) => row.name || `Scene ${index + 1}`)
            .map(name => `${name}`)
            .join(";");

        const lines = [`;${firstLine}`];

        objects.forEach((object) => {
            const line = [object];
            sceneData.forEach((scene) => {
                let value = '';
                if (scene.properties[object] !== undefined) {
                    value = scene.properties[object].toString();
                }
                line.push(value);
            });
            lines.push(line.map(v => `${v}`).join(';'));
        })

        const csvString = lines
            .join("\r\n");

        let csvContent = `data:text/csv;charset=utf-8,${csvString}`;

        const encodedUri = encodeURI(csvContent);

        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `exported-names.csv`);
        document.body.appendChild(link); // Required for FF

        link.click();
    }

    const startDraggingOnCanvas = (e: any) => {
        setCanvasDragPosition([e.clientX, e.clientY]);
    }
    const stopDraggingOnCanvas = () => {
        setCanvasDragPosition(false);
    }
    const dragOnCanvas = (e: any) => {
        if (!canvasDragPosition) return;
        const canvas = e.target as any as HTMLCanvasElement;
        const [startX, startY] = canvasDragPosition;
        const distanceX = e.clientX - startX;
        const distanceY = e.clientY - startY;

        const percentageX = distanceX / canvas.clientWidth;
        const percentageY = distanceY / canvas.clientHeight;

        // @ts-ignore
        gltf.scene.rotation.y += percentageX / 10;
        // @ts-ignore
        gltf.scene.rotation.x += percentageY / 10;
    }

    return (
        <div className="exporter-wrapper">
            <div className="uploader-wrapper">

                <div className="upload-button">
                    <input type="file" onChange={handleChange} />
                    <button>Upload OBJ</button>
                </div>
                <div className="upload-button">
                    <input type="file" onChange={handleCSVChange} />
                    <button>Upload CSV</button>
                </div>
                <button
                    disabled={!objects}
                    onClick={() => downloadCSV()}
                >Download CSV</button>
                <button
                    disabled={!objects}
                    onClick={() => downloadCSV()}
                >Export JSON</button>
            </div>
            <div className="settings-wrapper">
                <label htmlFor="sceneCount">
                    How many scenes do you want?
                </label>
                <input type="number" value={sceneCount} onChange={(event) => setSceneCount(parseInt(event.target.value)) } />
            </div>
            <div className="scene-wrapper">
                <table>
                    <thead className={'sticky sticky-top'}>
                    <tr>
                        <th>&nbsp;</th>
                        {(new Array(sceneCount)).fill(0).map((nothing, index) => {
                            return <td key={index}>
                                <div className={'scene-name-wrapper'}>
                                    <input type="checkbox"
                                           checked={activeScenes.includes(index)}
                                           disabled={!gltf}
                                           onChange={() => toggleActiveScene(index)}
                                    />
                                    <input
                                        placeholder={`Scene ${index + 1}`}
                                        type="text"
                                        value={sceneData[index]?.name ?? ''}
                                        size={(sceneData[index]?.name ?? '').length}
                                        onChange={(event) => setSceneName(event.target.value, index)}
                                    />
                                </div>
                            </td>
                        })}
                    </tr>
                    </thead>
                    <tbody>
                    {objects.map((name, objectIndex) => {
                        return (
                            <tr key={objectIndex}>
                                <td className={'sticky sticky-left'}>{name}</td>
                                {(new Array(sceneCount)).fill(0).map((nothing, index) => {
                                    return <td key={index}
                                               onMouseDown={() => setIsDraggingOnField([index, name])}
                                               onMouseUp={() => setIsDraggingOnField(false)}
                                               onMouseMove={() => copyFieldDataIfDragging(index, name)}
                                    >
                                        <input
                                            type="number"
                                            value={!sceneData[index] ? '' : (sceneData[index].properties[name] ?? '')}
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            style={{width: '100%'}}
                                            onChange={(event) => handleDeltaChange(parseFloat(event.target.value), name, index)}
                                            size={!sceneData[index] ? 3 : ((sceneData[index].properties[name] ?? '').toString().length)}
                                        />
                                    </td>
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <div className="canvas-wrapper">
                <canvas
                    ref={canvasRef}
                    onMouseDown={startDraggingOnCanvas}
                    onMouseUp={stopDraggingOnCanvas}
                    onMouseMove={dragOnCanvas}
                />
            </div>
        </div>
    )
}
